/*
 * Super Admin → Founding Partners.
 * Manage the 60-day Founding Partner program: KPIs, a partner table with
 * computed status + days remaining + activity, manual extension, and an
 * on-demand "Send due notifications" action (14/7/1/expiration emails).
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Users, Clock, AlertTriangle, CheckCircle2, MailCheck, Timer, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  "Active": "bg-[#dcfce7] text-[#166534] border-[#86efac]",
  "Expiring Soon": "bg-[#fef9c3] text-[#854d0e] border-[#fde047]",
  "Expired": "bg-[#fee2e2] text-[#991b1b] border-[#fca5a5]",
  "Declined Paid Conversion": "bg-[#f3f4f6] text-[#374151] border-[#d1d5db]",
  "Converted to Paid": "bg-[#dbeafe] text-[#1e40af] border-[#93c5fd]",
  "Invited": "bg-[#ede9fe] text-[#5b21b6] border-[#c4b5fd]",
};

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-xl border border-[#e5e7eb] bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[#6b7280]"><Icon className="h-4 w-4" /><span className="text-[11.5px] uppercase tracking-wide font-semibold">{label}</span></div>
    <div className="mt-1.5 text-2xl font-bold text-[#0f172a]">{value}</div>
  </div>
);

export default function FoundingPartnersPage({ session }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendDays, setExtendDays] = useState("30");
  const [warnDays, setWarnDays] = useState("");
  const [grantOpen, setGrantOpen] = useState(false);
  const [grant, setGrant] = useState({ email: "", free_days: "365", warn: "" });
  const headers = { "x-user-role": session?.user?.role || "admin", "x-user-id": session?.user?.id || "" };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/founding-partners`, { headers });
      setData(r.data);
    } catch { toast.error("Failed to load Founding Partners"); }
  }, [JSON.stringify(headers)]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const sendNotifications = async () => {
    setBusy("notify");
    try {
      const { data: r } = await axios.post(`${API}/api/admin/founding-partners/send-notifications`, {}, { headers });
      toast.success(r.sent_count > 0 ? `Sent ${r.sent_count} notification${r.sent_count === 1 ? "" : "s"}` : "No notifications were due");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); } finally { setBusy(""); }
  };

  const doExtend = async () => {
    setBusy("extend");
    try {
      await axios.post(`${API}/api/admin/founding-partners/${extendTarget.kind}/${extendTarget.id}/extend`,
        { days: parseInt(extendDays, 10) || 30, expiring_soon_days: warnDays ? (parseInt(warnDays, 10) || null) : null }, { headers });
      toast.success(`Extended by ${extendDays} days`);
      setExtendTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Extend failed"); } finally { setBusy(""); }
  };

  const doGrant = async () => {
    if (!grant.email.trim()) { toast.error("Enter the Portfolio Owner's email"); return; }
    setBusy("grant");
    try {
      const { data: r } = await axios.post(`${API}/api/admin/founding-partners/grant`,
        { email: grant.email.trim(), free_days: parseInt(grant.free_days, 10) || 365,
          expiring_soon_days: grant.warn ? (parseInt(grant.warn, 10) || null) : null }, { headers });
      toast.success(`${r.email} is now a Founding Partner (${r.fp?.days_remaining ?? "?"} days)`);
      setGrantOpen(false); setGrant({ email: "", free_days: "365", warn: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Grant failed"); } finally { setBusy(""); }
  };

  if (!data) return <div className="py-16 text-center text-[#6b7280]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  const k = data.kpis;

  return (
    <div data-testid="founding-partners-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-[26px] font-bold text-[#0f172a]" data-testid="fp-title">Founding Partners</h1>
          <p className="mt-1 text-[13px] text-[#64748b]">Manage the 60-day Founding Partner program — access, expirations and conversions.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setGrantOpen(true)} variant="outline" className="border-[#0b1220]" data-testid="fp-grant-open">
            <Users className="h-4 w-4 mr-2" />Grant Founding Partner
          </Button>
          <Button onClick={sendNotifications} disabled={busy === "notify"} className="bg-[#0b1220] hover:bg-[#111c33] text-white" data-testid="fp-send-notifications">
            {busy === "notify" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send due notifications
          </Button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="fp-kpis">
        <Kpi icon={Users} label="Active" value={k.active} testid="fp-kpi-active" />
        <Kpi icon={Timer} label="Expiring in 14d" value={k.expiring_14} testid="fp-kpi-expiring" />
        <Kpi icon={AlertTriangle} label="Expired" value={k.expired} testid="fp-kpi-expired" />
        <Kpi icon={CheckCircle2} label="Converted to Paid" value={k.converted} testid="fp-kpi-converted" />
        <Kpi icon={Clock} label="Avg days remaining" value={k.avg_days_remaining} testid="fp-kpi-avgdays" />
        <Kpi icon={MailCheck} label="Diagnostics completed" value={k.diagnostics_completed} testid="fp-kpi-diagnostics" />
        <Kpi icon={MailCheck} label="Feedback received" value={k.feedback_received} testid="fp-kpi-feedback" />
        <Kpi icon={Users} label="Total partners" value={k.total} testid="fp-kpi-total" />
      </div>

      <div className="mt-6 rounded-xl border border-[#e5e7eb] bg-white overflow-x-auto">
        <table className="w-full min-w-[900px] text-[13px]">
          <thead className="bg-[#f9fafb] text-[11.5px] uppercase tracking-wide text-[#6b7280]">
            <tr className="text-left">
              <th className="px-4 py-2.5">Founding Partner</th><th className="px-3">Type</th><th className="px-3">Status</th>
              <th className="px-3">Days left</th><th className="px-3">Activation</th><th className="px-3">End</th>
              <th className="px-3">Diagnostics</th><th className="px-3">Feedback</th><th className="px-3">Sales contact</th><th className="px-3 text-right pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.founding_partners.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-[#6b7280]">No Founding Partners yet.</td></tr>
            )}
            {data.founding_partners.map((r) => (
              <tr key={`${r.kind}-${r.id}`} className="border-t border-[#f0f1f3]" data-testid={`fp-row-${r.id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-semibold text-[#111827]">{r.organization || "—"}</div>
                  <div className="text-[11.5px] text-[#6b7280]">{r.name} · {r.email}</div>
                </td>
                <td className="px-3 capitalize">{r.kind}</td>
                <td className="px-3">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11.5px] font-semibold ${STATUS_STYLE[r.status] || "bg-[#f3f4f6] text-[#374151] border-[#d1d5db]"}`} data-testid={`fp-status-${r.id}`}>{r.status}</span>
                </td>
                <td className="px-3 font-semibold">{r.days_remaining ?? "—"}</td>
                <td className="px-3 text-[#6b7280]">{r.start_date ? r.start_date.slice(0, 10) : "—"}</td>
                <td className="px-3 text-[#6b7280]">{r.end_date ? r.end_date.slice(0, 10) : "—"}</td>
                <td className="px-3">{r.diagnostics_completed}/{r.diagnostics_started}</td>
                <td className="px-3">{r.feedback}</td>
                <td className="px-3 text-[#6b7280]">{r.sales_contact_name || "—"}</td>
                <td className="px-3 text-right pr-4">
                  {r.status !== "Converted to Paid" && (
                    <Button size="sm" variant="outline" onClick={() => { setExtendTarget(r); setExtendDays("30"); setWarnDays(""); }} data-testid={`fp-extend-${r.id}`}>Extend</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!extendTarget} onOpenChange={(v) => !v && setExtendTarget(null)}>
        <DialogContent className="max-w-sm bg-white" data-testid="fp-extend-modal">
          <DialogHeader><DialogTitle>Extend Founding Partner access</DialogTitle></DialogHeader>
          <p className="text-[13px] text-[#374151]">Add days to <b>{extendTarget?.organization}</b>'s access period.</p>
          <Input type="number" min="1" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} data-testid="fp-extend-days" />
          <p className="text-[12px] text-[#6b7280] mt-3">Early-warning window (optional): show the "Expiring Soon" nudge & paid-conversion prompt this many days before the end date. Leave blank for the default (14 days). Example: 60 = start the paid conversation ~2 months before a 12-month term ends.</p>
          <Input type="number" min="1" placeholder="e.g. 60" value={warnDays} onChange={(e) => setWarnDays(e.target.value)} data-testid="fp-extend-warn-days" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setExtendTarget(null)}>Cancel</Button>
            <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" disabled={busy === "extend"} onClick={doExtend} data-testid="fp-extend-confirm">
              {busy === "extend" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Extend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent className="max-w-sm bg-white" data-testid="fp-grant-modal">
          <DialogHeader><DialogTitle>Grant Founding Partner status</DialogTitle></DialogHeader>
          <p className="text-[13px] text-[#374151]">Flag an existing <b>Portfolio Owner</b> account as a Founding Partner and start their complimentary free-access window. Use this after a portfolio owner signs up via a sales rep's tracked link, so they keep salesperson attribution and still get free access.</p>
          <label className="text-[12px] font-medium text-[#374151] mt-2">Portfolio Owner email</label>
          <Input type="email" placeholder="owner@organization.org" value={grant.email} onChange={(e) => setGrant({ ...grant, email: e.target.value })} data-testid="fp-grant-email" />
          <label className="text-[12px] font-medium text-[#374151] mt-2">Free access (days)</label>
          <Input type="number" min="1" value={grant.free_days} onChange={(e) => setGrant({ ...grant, free_days: e.target.value })} data-testid="fp-grant-days" />
          <p className="text-[12px] text-[#6b7280] mt-2">Early-warning window (optional): show the paid-conversion nudge this many days before the end date (blank = default 14). Example: 61 = start the conversation ~2 months before a 365-day term ends.</p>
          <Input type="number" min="1" placeholder="e.g. 61" value={grant.warn} onChange={(e) => setGrant({ ...grant, warn: e.target.value })} data-testid="fp-grant-warn" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setGrantOpen(false)}>Cancel</Button>
            <Button className="bg-[#0b1220] hover:bg-[#111c33] text-white" disabled={busy === "grant"} onClick={doGrant} data-testid="fp-grant-confirm">
              {busy === "grant" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Grant Founding Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
